// Playful beach creatures animated across the screen at intervals.
// Click each: reveals a speech bubble.
// Find all 3 (unique clicks): unlocks 10% off discount valid 3 minutes (server-issued token).
import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { api } from "@/lib/api";
import CartoonCrab from "@/components/creatures/CartoonCrab";
import CartoonTurtle from "@/components/creatures/CartoonTurtle";
import CartoonSeagull from "@/components/creatures/CartoonSeagull";

const HIDE_ON_PATHS = ["/admin", "/reserva-confirmada"];
const SESSION_KEY = "vpv_creatures_session";
const FOUND_KEY = "vpv_creatures_found_v1";
const BONUS_KEY = "vpv_creatures_bonus_v1"; // { token, expiresAt }

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CHARACTERS = {
  crab: {
    img: "/creatures/crab.png",
    es: { name: "Lalo el cangrejo", message: "¡Ehh! Me caché entre la arena. Soy guardián de Barra de Galindo. Hazme el quiz y te suelto 10% off por reservar directo.", cta: "Hacer el quiz" },
    en: { name: "Lalo the crab", message: "Hey! Caught me in the sand. I'm Barra de Galindo's guardian. Take the quiz and I'll drop 10% off direct booking.", cta: "Take the quiz" },
    href: "/quiz",
    bg: "#E07A5F",
  },
  turtle: {
    img: "/creatures/turtle.png",
    es: { name: "Tati la tortuga", message: "¿Vas muy rápido? Aquí el ritmo es del mar. Echa un ojo al blog mientras descansas.", cta: "Leer el blog" },
    en: { name: "Tati the turtle", message: "Going too fast? Here we move at the sea's pace. Browse the blog while you rest.", cta: "Read the blog" },
    href: "/blog",
    bg: "#81B29A",
  },
  bird: {
    img: "/creatures/bird.png",
    es: { name: "Vita la gaviota", message: "Volé desde el atardecer. Tres villas frente al mar te esperan. ¿Reservamos?", cta: "Ver disponibilidad" },
    en: { name: "Vita the gull", message: "I flew in from the sunset. Three villas by the sea await. Shall we book?", cta: "See availability" },
    href: "/#reservar",
    bg: "#3A7CA5",
  },
};

export default function BeachCreatures() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { lang } = useLang();
  const [active, setActive] = useState({ crab: false, turtle: false, bird: false });
  const [bubble, setBubble] = useState(null);
  const [bookingActive, setBookingActive] = useState(false);

  // Listen for booking-active signal from <Booking>
  useEffect(() => {
    const handler = (e) => setBookingActive(!!e.detail?.active);
    window.addEventListener("vpv:booking-active", handler);
    return () => window.removeEventListener("vpv:booking-active", handler);
  }, []);
  const [bonus, setBonus] = useState(() => {
    try {
      const raw = localStorage.getItem(BONUS_KEY);
      if (!raw) return null;
      const b = JSON.parse(raw);
      return new Date(b.expiresAt).getTime() > Date.now() ? b : null;
    } catch { return null; }
  });
  const [now, setNow] = useState(Date.now());

  const hidden = HIDE_ON_PATHS.some((p) => pathname.startsWith(p)) || bookingActive;

  // Bonus countdown
  useEffect(() => {
    if (!bonus) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [bonus]);
  const bonusSecondsLeft = bonus ? Math.max(0, Math.floor((new Date(bonus.expiresAt).getTime() - now) / 1000)) : 0;
  const bonusActive = !!bonus && bonusSecondsLeft > 0;
  useEffect(() => {
    if (bonus && bonusSecondsLeft === 0) {
      setBonus(null);
      try { localStorage.removeItem(BONUS_KEY); } catch {}
      toast.info(lang === "es" ? "Tu bonus de 10% expiró" : "Your 10% bonus expired");
    }
  }, [bonus, bonusSecondsLeft, lang]);

  const fmtCountdown = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  // Creature scheduling — runs once per browser session, persists across navigation
  useEffect(() => {
    if (hidden) return;
    const sessionStart = parseInt(sessionStorage.getItem("vpv_creatures_start") || "0", 10);
    const now = Date.now();
    let pageStart;
    if (!sessionStart) {
      pageStart = now;
      sessionStorage.setItem("vpv_creatures_start", String(pageStart));
    } else {
      pageStart = sessionStart;
    }

    const elapsed = (now - pageStart) / 1000; // seconds since page first load
    const shown = JSON.parse(sessionStorage.getItem("vpv_creatures_shown") || "[]");

    const schedule = [
      { key: "crab", at: 45, duration: 14000 },
      { key: "turtle", at: 75, duration: 20000 },
      { key: "bird", at: 105, duration: 11000 },
    ];

    const timers = [];
    schedule.forEach(({ key, at, duration }) => {
      if (shown.includes(key)) return; // already appeared this session, skip
      const delay = Math.max(0, (at - elapsed) * 1000);
      timers.push(setTimeout(() => {
        setActive((s) => ({ ...s, [key]: true }));
        try {
          const list = JSON.parse(sessionStorage.getItem("vpv_creatures_shown") || "[]");
          if (!list.includes(key)) {
            sessionStorage.setItem("vpv_creatures_shown", JSON.stringify([...list, key]));
          }
        } catch {}
        timers.push(setTimeout(() => setActive((s) => ({ ...s, [key]: false })), duration));
      }, delay));
    });

    return () => timers.forEach(clearTimeout);
  }, [hidden]);

  const unlockBonus = useCallback(async () => {
    // Request a 3-min unlock token from backend
    try {
      const { data } = await api.post("/creatures/unlock", {});
      const b = { token: data.unlock_token, expiresAt: data.expires_at };
      setBonus(b);
      try { localStorage.setItem(BONUS_KEY, JSON.stringify(b)); } catch {}
      setNow(Date.now());
      toast.success(lang === "es"
        ? "🌟 ¡Encontraste a los 3 amigos! 10% off por 3 minutos."
        : "🌟 You found all 3 friends! 10% off for 3 minutes.");
    } catch (e) {
      toast.error(lang === "es" ? "No pudimos activar el bonus" : "Couldn't activate bonus");
    }
  }, [lang]);

  const handleClick = useCallback((key, e) => {
    e.stopPropagation();
    setActive((s) => ({ ...s, [key]: false }));
    setBubble({ key, x: e.clientX, y: e.clientY });
    try {
      const found = JSON.parse(localStorage.getItem(FOUND_KEY) || "[]");
      if (!found.includes(key)) {
        const updated = [...found, key];
        localStorage.setItem(FOUND_KEY, JSON.stringify(updated));
        if (updated.length === 3 && !bonus) {
          setTimeout(unlockBonus, 800);
        }
      }
    } catch {}
  }, [bonus, unlockBonus]);

  const closeBubble = () => setBubble(null);
  const goCta = () => {
    if (!bubble) return;
    const c = CHARACTERS[bubble.key];
    closeBubble();
    if (c.href.startsWith("/#")) {
      const anchor = c.href.slice(2);
      if (pathname === "/") document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth" });
      else { navigate("/"); setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth" }), 300); }
    } else navigate(c.href);
  };

  const goReserveWithBonus = () => {
    if (pathname === "/") document.getElementById("reservar")?.scrollIntoView({ behavior: "smooth" });
    else { navigate("/"); setTimeout(() => document.getElementById("reservar")?.scrollIntoView({ behavior: "smooth" }), 300); }
  };

  if (hidden) return null;

  return (
    <>
      <style>{`
        @keyframes crabSideWalk {
          0% { left: -120px; transform: translateY(0) rotate(0deg); }
          50% { left: 50%; }
          100% { left: calc(100% + 60px); transform: translateY(0) rotate(0deg); }
        }
        @keyframes crabBob {
          0%, 100% { transform: translateY(0) rotate(-2deg) scaleX(1); }
          25% { transform: translateY(-6px) rotate(2deg) scaleX(0.97); }
          50% { transform: translateY(0) rotate(-2deg) scaleX(1); }
          75% { transform: translateY(-6px) rotate(2deg) scaleX(0.97); }
        }
        @keyframes turtleCrawl {
          0% { left: calc(100% + 60px); }
          100% { left: -180px; }
        }
        @keyframes turtleBob {
          0%, 100% { transform: translateY(0) scaleX(-1); }
          50% { transform: translateY(-3px) scaleX(-1); }
        }
        @keyframes birdFlight {
          0% { left: -160px; transform: translateY(0); }
          25% { left: 25%; transform: translateY(-40px); }
          50% { left: 50%; transform: translateY(10px); }
          75% { left: 75%; transform: translateY(-30px); }
          100% { left: calc(100% + 80px); transform: translateY(0); }
        }
        @keyframes birdFlap { 0%, 100% { transform: scaleY(1); } 50% { transform: scaleY(0.6) translateY(-4px); } }
        @keyframes bubbleIn {
          0% { opacity: 0; transform: translate(-50%, -110%) scale(0.85); }
          100% { opacity: 1; transform: translate(-50%, -110%) scale(1); }
        }
        @keyframes bonusBarShimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes bonusPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.5); }
          50% { box-shadow: 0 0 0 14px rgba(220, 38, 38, 0); }
        }
      `}</style>

      {/* Crab */}
      {active.crab && (
        <div
          data-testid="creature-crab"
          className="fixed bottom-2 sm:bottom-3 z-30 cursor-pointer select-none"
          style={{ animation: "crabSideWalk 14s linear forwards" }}
          onClick={(e) => handleClick("crab", e)}
        >
          <div className="hover:scale-110 transition-transform" style={{ filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.25))" }}>
            <CartoonCrab className="w-28 h-24 sm:w-32 sm:h-28" />
          </div>
        </div>
      )}

      {/* Turtle */}
      {active.turtle && (
        <div
          data-testid="creature-turtle"
          className="fixed bottom-1 sm:bottom-2 z-30 cursor-pointer select-none"
          style={{ animation: "turtleCrawl 20s linear forwards" }}
          onClick={(e) => handleClick("turtle", e)}
        >
          <div className="hover:scale-110 transition-transform" style={{ filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.25))", transform: "scaleX(-1)" }}>
            <CartoonTurtle className="w-32 h-24 sm:w-36 sm:h-28" />
          </div>
        </div>
      )}

      {/* Bird */}
      {active.bird && (
        <div
          data-testid="creature-bird"
          className="fixed top-20 sm:top-24 z-30 cursor-pointer select-none"
          style={{ animation: "birdFlight 11s ease-in-out forwards" }}
          onClick={(e) => handleClick("bird", e)}
        >
          <div className="hover:scale-110 transition-transform" style={{ filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.25))" }}>
            <CartoonSeagull className="w-28 h-24 sm:w-32 sm:h-28" />
          </div>
        </div>
      )}

      {/* Speech bubble */}
      {bubble && (() => {
        const c = CHARACTERS[bubble.key];
        const tt = c[lang] || c.es;
        const left = Math.min(Math.max(bubble.x, 180), window.innerWidth - 180);
        const top = Math.max(bubble.y, 200);
        return (
          <>
            <div className="fixed inset-0 z-40" onClick={closeBubble} data-testid={`creature-bubble-${bubble.key}`} />
            <div role="dialog" className="fixed z-50 w-[300px] max-w-[calc(100vw-2rem)]"
                 style={{ left: `${left}px`, top: `${top}px`, animation: "bubbleIn 0.35s cubic-bezier(0.16,1,0.3,1) both" }}>
              <div className="relative bg-white border-2 border-stone-900 shadow-2xl">
                <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-r-2 border-b-2 border-stone-900" style={{ transform: "translate(-50%, 50%) rotate(45deg)" }} />
                <div className="px-5 pt-5 pb-4">
                  <div className="flex items-center gap-3 mb-3">
                    {bubble.key === "crab" && <div className="w-14 h-12"><CartoonCrab className="w-full h-full" /></div>}
                    {bubble.key === "turtle" && <div className="w-14 h-12"><CartoonTurtle className="w-full h-full" /></div>}
                    {bubble.key === "bird" && <div className="w-14 h-12"><CartoonSeagull className="w-full h-full" /></div>}
                    <p className="font-serif-display text-xl leading-tight">{tt.name}</p>
                  </div>
                  <p className="text-sm text-stone-700 leading-relaxed mb-4">{tt.message}</p>
                  <div className="flex items-center gap-2">
                    <button onClick={goCta} data-testid={`creature-cta-${bubble.key}`}
                            className="flex-1 text-white text-xs uppercase tracking-[0.15em] font-bold px-4 py-3 hover:opacity-90 transition-opacity"
                            style={{ background: c.bg }}>{tt.cta}</button>
                    <button onClick={closeBubble} data-testid={`creature-close-${bubble.key}`}
                            className="text-xs text-stone-500 px-3 py-3 hover:text-stone-900 transition-colors">{lang === "es" ? "Cerrar" : "Close"}</button>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* Bonus sticky bar — visible while 3-min countdown is running */}
      {bonusActive && (
        <button
          onClick={goReserveWithBonus}
          data-testid="creature-bonus-bar"
          className="fixed bottom-0 left-0 right-0 z-[55] text-white py-3 px-4 flex items-center justify-center gap-3 text-sm md:text-base font-bold hover:brightness-110 transition-all"
          style={{
            backgroundImage: "linear-gradient(90deg, #DC2626, #EF4444, #DC2626)",
            backgroundSize: "200% 100%",
            animation: "bonusBarShimmer 4s linear infinite, bonusPulse 2s ease-in-out infinite",
          }}
        >
          <span className="text-2xl">🌟</span>
          <span>{lang === "es" ? "¡Bonus desbloqueado!" : "Bonus unlocked!"}</span>
          <span className="font-black">10% OFF</span>
          <span className="opacity-90">·</span>
          <span className="opacity-90">{lang === "es" ? "Expira en" : "Expires in"}</span>
          <span className="font-mono font-black tabular-nums bg-white/20 px-2 py-0.5 rounded">{fmtCountdown(bonusSecondsLeft)}</span>
          <span className="hidden sm:inline">→</span>
        </button>
      )}
    </>
  );
}
